/**
 * LedgerDashboard — read-only view in UniHub that pulls from the ledger API.
 * Replaces the old Firestore-backed ReconApp.
 *
 * Requires business.ledgerEntityId to be set (mapping UniHub business -> ledger entity).
 */
import React, { useEffect, useState } from 'react';
import { ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import ledgerApi from '../lib/ledgerApi';

function fmt(n, ccy = '') {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const abs = Math.abs(Number(n));
  const sign = n < 0 ? '-' : '';
  return `${sign}${ccy} ${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
}

function fmtDate(d) { return d ? String(d).substring(0, 10) : ''; }

export default function LedgerDashboard({ business }) {
  const entityId = business.ledgerEntityId;
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [snap, setSnap] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pnlPeriod, setPnlPeriod] = useState('month');

  const refresh = async () => {
    if (!entityId) {
      setErr('No ledger entity linked. Set "Ledger entity ID" in Settings.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const [s, k, a] = await Promise.all([
        ledgerApi.snapshot(entityId),
        ledgerApi.kpi(entityId),
        ledgerApi.capexAssets(entityId).catch(() => []),
      ]);
      setSnap(s); setKpi(k); setAssets(a || []);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [entityId]);

  useEffect(() => {
    if (!entityId) return;
    if (tab === 'pnl') {
      ledgerApi.pnl(entityId, { period: pnlPeriod }).then(setPnl).catch((e) => setErr(e.message));
    }
    if (tab === 'bs') {
      ledgerApi.balanceSheet(entityId).then(setBs).catch((e) => setErr(e.message));
    }
  }, [tab, pnlPeriod, entityId]);

  if (!entityId) {
    return (
      <div className="card" style={{ padding: 20, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--red, #a83238)' }}>
          <AlertTriangle size={18} />
          <strong>This business is not linked to a ledger entity.</strong>
        </div>
        <p style={{ marginTop: 10, color: 'var(--muted, #6b6b6b)' }}>
          Open Settings and set the <code>Ledger entity ID</code> (e.g. <code>kng</code>, <code>rr</code>, <code>white-manta</code>)
          to enable this view.
        </p>
      </div>
    );
  }

  const openLedger = (view = 'dashboard') => {
    window.open(ledgerApi.deepLink(entityId, view), '_blank');
  };

  return (
    <div className="ledger-dashboard">
      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            ['dashboard', 'Dashboard'],
            ['pnl', 'P&L'],
            ['bs', 'Balance Sheet'],
            ['capex', 'CapEx'],
          ].map(([k, label]) => (
            <button
              key={k}
              className={`btn btn-sm ${tab === k ? 'btn-primary' : ''}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onClick={refresh} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary btn-sm" onClick={() => openLedger(tab === 'capex' ? 'capex' : tab === 'pnl' ? 'pnl' : tab === 'bs' ? 'bs' : 'dashboard')}>
            <ExternalLink size={14} /> Open in Ledger
          </button>
        </div>
      </div>

      {err && <div className="error-banner" style={{ padding: 10, background: '#fdeaea', color: '#a83238', borderRadius: 6, marginBottom: 12 }}>
        Error: {err}
      </div>}

      {loading && <div style={{ color: 'var(--muted)' }}>Loading…</div>}

      {!loading && tab === 'dashboard' && snap && (
        <DashboardView snap={snap} kpi={kpi} />
      )}
      {!loading && tab === 'pnl' && (
        <PnLView data={pnl} period={pnlPeriod} setPeriod={setPnlPeriod} />
      )}
      {!loading && tab === 'bs' && (
        <BalanceSheetView data={bs} />
      )}
      {!loading && tab === 'capex' && (
        <CapexView assets={assets} />
      )}
    </div>
  );
}

// ---------------------------------------------------------
// Subviews
// ---------------------------------------------------------
function DashboardView({ snap, kpi }) {
  const ccy = snap?.entity?.base_currency || '';
  const kpis = [
    { label: 'Ledger Balance', value: fmt(snap.ledger_total, ccy) },
    { label: 'This Month Revenue', value: fmt(snap.month.revenue, ccy), class: 'positive' },
    { label: 'This Month Expenses', value: fmt(snap.month.expenses, ccy), class: 'negative' },
    { label: 'Net', value: fmt(snap.month.net, ccy), class: snap.month.net >= 0 ? 'positive' : 'negative' },
    { label: 'Pending Recon', value: snap.pending_reconciliation },
    { label: 'Cash Runway', value: kpi?.cash_runway_months ? `${kpi.cash_runway_months.toFixed(1)} mo` : (kpi?.avg_monthly_burn ? '—' : '∞') },
  ];
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.label} className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--muted, #6b6b6b)', letterSpacing: 0.4 }}>{k.label}</div>
            <div className={k.class} style={{ fontSize: 20, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
          </div>
        ))}
      </div>
      <h3 style={{ margin: '12px 0', fontSize: 14, textTransform: 'uppercase', color: 'var(--muted)' }}>Account Balances</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {snap.accounts.map((a) => (
          <div key={a.id} className="card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <strong>{a.name}</strong>
              <span style={{ fontSize: 11, background: '#e4e4e2', padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase' }}>{a.type}</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(a.ledger_balance, a.currency)}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>In: {fmt(a.cash_in, a.currency)} · Out: {fmt(a.cash_out, a.currency)}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function PnLView({ data, period, setPeriod }) {
  if (!data) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;
  const ccy = data.currency;
  const t = data.totals;
  const group = (g, isNeg) => Object.keys(g.sections).length ? (
    <>
      <tr><td colSpan={2} style={{ paddingTop: 16, color: isNeg ? '#a83238' : '#1f7a4d', fontWeight: 600 }}>{g.label}</td></tr>
      {Object.values(g.sections).map((s) => (
        <React.Fragment key={s.label}>
          <tr><td style={{ fontWeight: 500 }}>{s.label}</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(s.total, ccy)}</td></tr>
          {Object.values(s.categories).map((c) => (
            <React.Fragment key={c.label}>
              <tr><td style={{ paddingLeft: 20 }}>{c.label}</td><td style={{ textAlign: 'right' }}>{fmt(c.total, ccy)}</td></tr>
              {c.items.map((i, idx) => (
                <tr key={idx}><td style={{ paddingLeft: 40, color: 'var(--muted)', fontSize: 12 }}>{i.name}</td><td style={{ textAlign: 'right', color: 'var(--muted)', fontSize: 12 }}>{fmt(i.amount, ccy)}</td></tr>
              ))}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  ) : null;
  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="month">This month</option>
          <option value="ytd">Year to date</option>
          <option value="last_12m">Last 12 months</option>
        </select>
        <div style={{ color: 'var(--muted)', fontSize: 13, alignSelf: 'center' }}>Period: {data.from} → {data.to}</div>
      </div>
      <table className="nk-table-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {group(data.groups.revenue)}
          {group(data.groups.cogs, true)}
          <tr><td style={{ fontWeight: 700, background: '#f3efe7', padding: 8 }}>Gross Profit ({t.gross_margin_pct.toFixed(1)}%)</td><td style={{ fontWeight: 700, textAlign: 'right', background: '#f3efe7', padding: 8 }}>{fmt(t.gross_profit, ccy)}</td></tr>
          {group(data.groups.operating_expenses, true)}
          <tr><td style={{ fontWeight: 700, background: '#f3efe7', padding: 8 }}>Operating Profit ({t.operating_margin_pct.toFixed(1)}%)</td><td style={{ fontWeight: 700, textAlign: 'right', background: '#f3efe7', padding: 8 }}>{fmt(t.operating_profit, ccy)}</td></tr>
          {group(data.groups.non_operating_income)}
          {group(data.groups.non_operating_expenses, true)}
          <tr><td style={{ fontWeight: 700, background: '#3a3a3a', color: '#fff', padding: 10, fontSize: 15 }}>Net Profit ({t.net_margin_pct.toFixed(1)}%)</td><td style={{ fontWeight: 700, textAlign: 'right', background: '#3a3a3a', color: '#fff', padding: 10, fontSize: 15 }}>{fmt(t.net_profit, ccy)}</td></tr>
        </tbody>
      </table>
    </>
  );
}

function BalanceSheetView({ data }) {
  if (!data) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;
  const ccy = data.currency;
  const section = (title, items, total, absKey) => (
    <>
      <h3 style={{ marginTop: 16, fontSize: 14, textTransform: 'uppercase', color: 'var(--muted)' }}>{title}</h3>
      <table className="nk-table-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {items.length ? items.map((a) => (
            <tr key={a.id}><td style={{ padding: 8 }}>{a.name} <span style={{ fontSize: 11, color: 'var(--muted)' }}>{a.type}</span></td>
              <td style={{ textAlign: 'right', padding: 8 }}>{fmt(absKey ? a[absKey] : a.balance, a.currency)}</td></tr>
          )) : <tr><td colSpan={2} style={{ padding: 8, color: 'var(--muted)' }}>None</td></tr>}
          <tr><td style={{ fontWeight: 700, padding: 8, borderTop: '2px solid #d4af37' }}>Total</td><td style={{ textAlign: 'right', fontWeight: 700, padding: 8, borderTop: '2px solid #d4af37' }}>{fmt(total, ccy)}</td></tr>
        </tbody>
      </table>
    </>
  );
  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>As of {data.as_of}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', marginBottom: 12 }}>{data.note}</div>
      {section('Assets', data.assets.items, data.assets.total)}
      {section('Offsets', data.offsets.items, data.offsets.total)}
      {section('Liabilities', data.liabilities.items, data.liabilities.total_abs, 'balance_abs')}
      <div style={{ marginTop: 20, padding: 16, background: '#fff', border: '1px solid #e4e4e2', borderRadius: 6 }}>
        <strong style={{ fontSize: 18 }}>Net Equity: {fmt(data.net_equity, ccy)}</strong>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Assets + Offsets − Liabilities</div>
      </div>
    </>
  );
}

function CapexView({ assets }) {
  if (!assets || !assets.length) return <p style={{ color: 'var(--muted)' }}>No CapEx assets registered.</p>;
  return (
    <table className="nk-table-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', background: '#fafaf8' }}>
          <th style={{ padding: 8 }}>Name</th>
          <th style={{ padding: 8 }}>Category</th>
          <th style={{ padding: 8 }}>Acquired</th>
          <th style={{ padding: 8, textAlign: 'right' }}>Cost</th>
          <th style={{ padding: 8, textAlign: 'right' }}>Accum. Dep</th>
          <th style={{ padding: 8, textAlign: 'right' }}>NBV</th>
          <th style={{ padding: 8 }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {assets.map((a) => (
          <tr key={a.id} style={{ opacity: a.status !== 'active' ? 0.6 : 1 }}>
            <td style={{ padding: 8 }}>{a.name}</td>
            <td style={{ padding: 8 }}>{a.category}</td>
            <td style={{ padding: 8 }}>{fmtDate(a.acquisition_date)}</td>
            <td style={{ padding: 8, textAlign: 'right' }}>{fmt(a.acquisition_cost, a.currency)}</td>
            <td style={{ padding: 8, textAlign: 'right', color: 'var(--muted)' }}>{fmt(a.accumulated_depreciation, a.currency)}</td>
            <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{fmt(a.net_book_value, a.currency)}</td>
            <td style={{ padding: 8 }}>{a.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
